import { useState, useEffect } from 'react';

import { apiClient, createAuthHeaders } from '@/lib/api-client';
import { firebaseAuth } from '@/lib/firebase';

// In-memory cache (shared across all hook instances)
let cachedValue: boolean | null = null;
let fetchPromise: Promise<boolean> | null = null;

async function fetchIsInternalUser(): Promise<boolean> {
  const user = firebaseAuth.currentUser;
  if (!user) return false;

  const token = await user.getIdToken();
  const response = await apiClient.get('/api/auth/me', {
    headers: createAuthHeaders(token),
  });
  return response.data?.user?.isInternalUser === true;
}

/**
 * Hook to check if the current user is an OptiSigns internal user.
 *
 * Internal users are platform admins OR users whose email domain matches
 * INTERNAL_USER_EMAIL_DOMAINS on the backend (defaults to `optisigns.com`).
 *
 * Used to let internal staff bypass env-scoped feature flags (e.g. BYOC)
 * even when the flag is off for general users.
 *
 * Fetches /auth/me once and caches the result in memory.
 */
export function useInternalUser(): { isInternalUser: boolean; loading: boolean } {
  const [isInternalUser, setIsInternalUser] = useState(cachedValue ?? false);
  const [loading, setLoading] = useState(cachedValue === null);

  useEffect(() => {
    if (cachedValue !== null) {
      setIsInternalUser(cachedValue);
      setLoading(false);
      return;
    }

    if (fetchPromise) {
      fetchPromise
        .then((value) => {
          setIsInternalUser(value);
          setLoading(false);
        })
        .catch(() => {
          setIsInternalUser(false);
          setLoading(false);
        });
      return;
    }

    fetchPromise = fetchIsInternalUser();
    fetchPromise
      .then((value) => {
        cachedValue = value;
        setIsInternalUser(value);
        setLoading(false);
      })
      .catch(() => {
        setIsInternalUser(false);
        setLoading(false);
      })
      .finally(() => {
        fetchPromise = null;
      });
  }, []);

  return { isInternalUser, loading };
}
